import { logoParaImpresion } from './qr-label';

/**
 * Listado imprimible de los clubes inscritos en un campamento.
 *
 * Se pagina a mano —no con saltos de página del navegador— porque el pie tiene que
 * decir "Página 3 de 15", y Chrome no soporta las cajas de margen de `@page` ni el
 * contador `pages`. Con las filas repartidas aquí, el total se sabe antes de imprimir
 * y cada hoja lleva su cabecera y su pie completos.
 *
 * Solo strings: ni DOM ni React. Quien llama abre el HTML en una ventana e imprime.
 */

const VERDE = '#284B32';

const PIE = "Powered by Lira's prod @ rights reserved";

/**
 * Filas por hoja. Sale de la altura útil de un A4 con margen de 10 mm, menos cabecera,
 * cabecera de tabla y pie. Los encabezados de zona ocupan una fila del reparto: por eso
 * se cuentan como un elemento más y no como algo aparte.
 *
 * El reparto solo cuadra si todas las filas miden lo mismo, y de eso se encarga el
 * `table-layout: fixed` con anchos fijos: sin él, la fila de zona (un `colspan` ancho)
 * reordena las columnas, un nombre largo pasa a dos líneas y la hoja se desborda a una
 * página en blanco de más.
 */
const FILAS_POR_HOJA = 32;

export const ORDENES = [
  { valor: 'alfabetico', etiqueta: 'Alfabético (A-Z)' },
  { valor: 'zona', etiqueta: 'Por zona' },
];

const escapar = valor =>
  String(valor ?? '').replace(/[&<>"]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));

const zonaDe = club => (typeof club.affiliatedTo === 'object' ? club.affiliatedTo?.zone ?? '' : '');
const nombreAfiliacion = club => (typeof club.affiliatedTo === 'object' ? club.affiliatedTo?.name ?? '' : '');

const plural = (n, singular, plural_) => `${n} ${n === 1 ? singular : plural_}`;

/** Alfabético puro, o por zona y dentro de cada zona alfabético. */
export function ordenarClubes(clubs, orden) {
  const porNombre = (a, b) => a.name.localeCompare(b.name, 'es');
  if (orden === 'zona') {
    return [...clubs].sort((a, b) => zonaDe(a).localeCompare(zonaDe(b), 'es') || porNombre(a, b));
  }
  return [...clubs].sort(porNombre);
}

/**
 * La lista de filas ya numerada. Con orden por zona se intercala una fila de grupo por
 * cada zona; la numeración sigue siendo continua porque el listado se usa para contar
 * clubes del campamento, no de la zona.
 */
function construirFilas(clubs, orden) {
  const items = [];
  let zonaActual = null;

  clubs.forEach((club, i) => {
    if (orden === 'zona') {
      const zona = zonaDe(club) || '(sin zona)';
      if (zona !== zonaActual) {
        zonaActual = zona;
        const cuantos = clubs.filter(c => (zonaDe(c) || '(sin zona)') === zona).length;
        items.push({ tipo: 'grupo', zona, cuantos });
      }
    }
    items.push({ tipo: 'club', club, numero: i + 1 });
  });

  return items;
}

function pintarItem(item) {
  if (item.tipo === 'grupo') {
    return `<tr class="grupo"><td colspan="5">${escapar(item.zona)} · ${plural(item.cuantos, 'club', 'clubes')}</td></tr>`;
  }

  const { club, numero } = item;
  const estado = club.status === false
    ? '<span class="off">Inactivo</span>'
    : '<span class="on">Activo</span>';

  return `<tr>
            <td class="num">${numero}</td>
            <td class="nombre">${escapar(club.name)}</td>
            <td>${escapar(nombreAfiliacion(club)) || '—'}</td>
            <td>${escapar(zonaDe(club)) || '—'}</td>
            <td>${estado}</td>
          </tr>`;
}

const ESTILOS = `
  @page { size: A4 portrait; margin: 10mm; }
  * { box-sizing: border-box; }
  body { margin: 0; background: #f4f1e9; color: #1b1b1b;
         font-family: 'Avenir Next', 'Trebuchet MS', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
         -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .sheet { width: 190mm; height: 277mm; margin: 0 auto; background: #fff;
           display: flex; flex-direction: column; }
  .sheet + .sheet { page-break-before: always; }
  .head { display: flex; align-items: center; gap: 4mm; padding-bottom: 3mm;
          border-bottom: 1px solid rgba(40, 75, 50, 0.15); }
  .head img { width: 16mm; height: 16mm; object-fit: contain; border-radius: 2mm; flex: none; }
  .head h1 { margin: 0; font-size: 13pt; font-weight: 700; color: ${VERDE}; line-height: 1.15; }
  .head .sub { margin: 1mm 0 0; font-size: 8pt; color: rgba(0,0,0,0.5); }
  table { width: 100%; border-collapse: collapse; margin-top: 4mm; table-layout: fixed; }
  thead th { background: ${VERDE}; color: #fff; font-size: 7.5pt; font-weight: 600; text-align: left;
             padding: 2mm 2.5mm; }
  tbody td { font-size: 8pt; padding: 1.6mm 2.5mm; border-bottom: 1px solid rgba(0,0,0,0.08);
             white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  tbody tr:nth-child(even) td { background: rgba(0,0,0,0.02); }
  tbody tr.grupo td { background: #eef3ee; color: ${VERDE}; font-weight: 700; font-size: 7.5pt;
                      letter-spacing: 0.02em; }
  .num { width: 10mm; color: rgba(0,0,0,0.38); }
  .nombre { font-weight: 600; }
  .on  { color: #1e5c30; }
  .off { color: #9d2f2f; }
  .foot { margin-top: auto; padding-top: 3mm; border-top: 1px solid rgba(40, 75, 50, 0.15);
          display: flex; justify-content: space-between; align-items: baseline;
          font-size: 7pt; color: rgba(0,0,0,0.38); }
  /* En pantalla la hoja simula el A4 completo —210x297 con los margenes dentro— para que
     la caja de contenido siga midiendo los 190mm de la impresion. Con width:190mm y el
     padding encima, las tres columnas no cabian y la tercera se cortaba en la vista previa. */
  @media screen {
    body { padding: 8mm 0; }
    .sheet { width: 210mm; height: 297mm; padding: 10mm; margin-bottom: 8mm;
             box-shadow: 0 8px 28px rgba(38,42,33,0.14); }
  }
  @media print  { body { background: #fff; padding: 0; } }
`;

/**
 * El listado completo, listo para imprimir o guardar como PDF.
 *
 * `orden` es 'alfabetico' o 'zona'. Los clubes llegan sin ordenar: ordenarlos es parte
 * del informe, para que el orden del papel y el número de fila no se contradigan.
 */
export function construirListadoClubes({ camp, clubs, orden = 'alfabetico' }) {
  const ordenados = ordenarClubes(clubs, orden);
  const items = construirFilas(ordenados, orden);

  const hojas = [];
  for (let i = 0; i < items.length; i += FILAS_POR_HOJA) hojas.push(items.slice(i, i + FILAS_POR_HOJA));
  if (hojas.length === 0) hojas.push([]);

  const logoUrl = logoParaImpresion(camp?.logoUrl);
  const campName = camp?.name ?? '';
  const fecha = new Date().toLocaleDateString('es-DO', { day: 'numeric', month: 'long', year: 'numeric' });
  const subtitulo = `Clubes inscritos · ${plural(ordenados.length, 'club', 'clubes')} · ${fecha}`;

  const cuerpo = hojas
    .map((hoja, i) => `
    <section class="sheet">
      <header class="head">
        ${logoUrl ? `<img src="${escapar(logoUrl)}" alt="">` : ''}
        <div>
          <h1>${escapar(campName)}</h1>
          <p class="sub">${escapar(subtitulo)}</p>
        </div>
      </header>
      <table>
        <colgroup>
          <col style="width: 10mm"><col><col style="width: 26mm"><col style="width: 42mm"><col style="width: 20mm">
        </colgroup>
        <thead>
          <tr><th>#</th><th>Club</th><th>Afiliación</th><th>Zona</th><th>Estado</th></tr>
        </thead>
        <tbody>${hoja.map(pintarItem).join('')}</tbody>
      </table>
      <footer class="foot">
        <span>${escapar(PIE)}</span>
        <span>Página ${i + 1} de ${hojas.length}</span>
      </footer>
    </section>`)
    .join('\n');

  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>Clubes inscritos — ${escapar(campName)}</title>
<style>${ESTILOS}</style>
</head>
<body>
${cuerpo}
</body>
</html>
`;
}

export { FILAS_POR_HOJA, PIE };
